/**
 * Root-level routes for partnership welcome pages.
 *
 * What: The canonical list of partnership slugs, the route path each one owns,
 *       and the predicates every route surface resolves them with.
 * Why: A partnership welcome page is where a partner sends its students and
 *      tutors, often from a printed handout or a WhatsApp message someone reads
 *      aloud. That argues for the shortest URL we can give it — `/upstars`, not
 *      `/partnerships/upstars`. Enumerating the slugs in one module is what
 *      makes a root-level route safe: the router, the sitemap, the prerender
 *      manifest and the lastmod table all read this list instead of pattern
 *      matching, so a page cannot be live on one surface and missing on another.
 * When: Update when a partnership page is published or retired.
 *
 * ## A partnership slug is a reserved top-level URL
 *
 * These routes sit at the root of the site, so adding a slug here permanently
 * spends a top-level path: `/upstars` can never afterwards become an app route,
 * an auth callback, or a marketing cluster. Two rules follow.
 *
 *   1. Routes are enumerated, never `/:slug` at the root. A root-level wildcard
 *      would swallow every unmatched URL in the app — 404s, typos like
 *      `/singup`, and any future route added by someone who never read this
 *      file — and render a partnership shell over them.
 *   2. A new slug must not collide with a path the app already uses. The
 *      reserved-word guard in `__tests__/partnershipRoutes.test.ts` fails the
 *      build if it does; add to that list rather than trusting review to catch
 *      it.
 *
 * ## This is not affiliate marketing
 *
 * The namespace is `partnerships`, deliberately not `partners`.
 * `server/models/Partner.ts`, `shared/partnerContract.ts` and the `?ref=`
 * attribution chain are the affiliate/referral system: tracked clicks, revenue
 * share, lead capture. This module is the unrelated thing that shares an
 * English word — free accounts given to a community programme as social good.
 * Nothing in `shared/partnerships/`, `src/content/partnerships/` or the pages
 * built on them may import, mirror or grow toward that system: no attribution
 * beacons, no `?ref=` links, no lead forms.
 *
 * ## Bundle-size contract
 *
 * No value imports, of any kind. `shared/seo/publicMarketingRoutes.ts` imports
 * this module and ships in the client bundle, so this stays a small table plus
 * pure string predicates — the same rule `shared/schools/schoolAreas.ts`
 * documents for the area catchments.
 */

/** Slugs of every published partnership welcome page. */
export const PARTNERSHIP_SLUGS = Object.freeze(['upstars'] as const);

export type PartnershipSlug = (typeof PARTNERSHIP_SLUGS)[number];

/** The root-level path a partnership slug owns, e.g. `/upstars`. */
export type PartnershipRoutePath = `/${PartnershipSlug}`;

const PARTNERSHIP_SLUG_SET: ReadonlySet<string> = new Set(PARTNERSHIP_SLUGS);

/** The route path for a partnership slug. Root-level by design; see above. */
export function partnershipRoutePath(
  slug: PartnershipSlug
): PartnershipRoutePath {
  return `/${slug}`;
}

/** Every published partnership route path, in `PARTNERSHIP_SLUGS` order. */
export const PARTNERSHIP_ROUTE_PATHS: readonly PartnershipRoutePath[] =
  Object.freeze(PARTNERSHIP_SLUGS.map(partnershipRoutePath));

/** Whether a value is a published partnership slug. */
export function isPartnershipSlug(value: unknown): value is PartnershipSlug {
  return typeof value === 'string' && PARTNERSHIP_SLUG_SET.has(value);
}

/**
 * Whether a value is a published partnership route path.
 *
 * Membership, not shape: `/atlantis` is a well-formed root path and still
 * returns false, so an unpublished slug can never resolve a page shell.
 */
export function isPartnershipRoutePath(
  value: unknown
): value is PartnershipRoutePath {
  if (typeof value !== 'string') return false;
  if (!value.startsWith('/')) return false;
  return isPartnershipSlug(value.slice(1));
}
